import { CVEditingSection, FullCV } from '@cibli/shared';
import React, { useMemo } from 'react';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import type { EditableSectionSplit } from '../../../hooks/usePageDelimitation';

import EditableSection from '../../../components/form/EditableSection';
import { isNotEmpty } from '../../../utils/is-not-empty';

interface EducationProps {
  education: FullCV['education'];
  onEdit?: (section: CVEditingSection) => void;
  chunk?: EditableSectionSplit;
  headingColor?: string;
}

const Education: React.FC<EducationProps> = ({ education, onEdit, chunk, headingColor }) => {
  const { t, formatDate } = useCVTranslation();

  const handleEdit = () => {
    if (onEdit) {
      onEdit('education');
    }
  };

  const editable = useMemo(() => onEdit !== undefined, [onEdit]);

  if (!education?.length && !editable) return null;

  const start = chunk?.startIndex ?? 0;
  const end = chunk?.endIndex ?? education?.length ?? 0;
  const showTitle = chunk?.showTitle ?? true;
  const items = education?.slice(start, end) ?? [];

  return (
    <EditableSection
      onEdit={handleEdit}
      buttonPosition="right"
      editable={editable}
      splitable={true}
    >
      <div className="mb-2 p-2 m-2">
        {showTitle && (
          <div data-splitable-header>
            <h2
              className="text-[11px] font-black uppercase tracking-widest mb-0.25"
              style={{ color: headingColor ?? '#000000' }}
            >
              {t('CV.education')}
            </h2>
          </div>
        )}
        {!showTitle && <div className="h-2" aria-hidden />}
        <div>
          {items.map((edu, index) => {
            const formatDateRange = (start: string | null, end: string | null): string => {
              if (!start) return '';
              const startDateStr = formatDate(start, {
                month: 'short',
                year: 'numeric',
              });
              if (!startDateStr) return '';
              const endDateStr =
                end && end !== 'CURRENT'
                  ? formatDate(end, {
                      month: 'short',
                      year: 'numeric',
                    })
                  : '';
              if (endDateStr) return `${startDateStr} – ${endDateStr}`;
              return `${startDateStr} – ${t('CV.present')}`;
            };
            const dateRange = formatDateRange(edu.degreeStartedAt, edu.degreeObtainedAt);

            return (
              <div
                key={start + index}
                data-splitable-item
                className="mb-2 break-inside-avoid [page-break-inside:avoid]"
              >
                {isNotEmpty(edu.degree) && (
                  <h3
                    className="text-[14px] font-black uppercase mb-0.5 leading-tight"
                    style={{ color: headingColor ?? '#000000' }}
                  >
                    {(edu.degree ?? '').toUpperCase()}
                  </h3>
                )}
                {isNotEmpty(edu.school) && (
                  <p className="text-[14px] text-black font-semibold">{edu.school}</p>
                )}
                {dateRange && <p className="text-[12.75px] text-gray-600 italic">{dateRange}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </EditableSection>
  );
};

export default Education;
